/* GASSISA
=========================================================================================
 * Descripcion: Disenador de un bloque del ticket de ventas (datos, detalle o
 *              totales) basado en FILAS.
 *
 *              El diseno se hace en las mismas unidades que la impresora
 *              (dots @203 dpi) y se muestra en CENTIMETROS: el lienzo usa
 *              1 dot = 0.4725 px (96 dpi) y la cuadricula de 0.5 cm.
 *
 *              Conceptos (coherentes con ticketMotorLayout.js, el motor que
 *              arma el binario ESC/POS):
 *                - FILA: los objetos que comparten Y forman un renglon. En el
 *                  lienzo se ve como una banda horizontal.
 *                - X es ABSOLUTA: varios objetos pueden compartir la misma
 *                  linea fisica (etiqueta a la izquierda, valor a la derecha).
 *                - La vertical es FLUJO: la fila arranca en su ancla Y o donde
 *                  termino la anterior si crecio.
 *                - Ningun objeto se recorta: el texto se imprime completo y la
 *                  caja crece en alto segun sus saltos de linea.
 *                - El ancho es solo visual; no limita el texto. La impresion
 *                  parte las lineas que exceden el transporte de 96 caracteres
 *                  conservando la X del objeto; la impresora ajusta al ancho
 *                  fisico del papel.
 *
 *              El lienzo se arma con el PLAN del motor (mismo codigo que usa la
 *              impresion), de modo que lo que se ve es lo que sale en papel.
 *
 * Historial de cambios:
 * ---------------------------------------------------------------------------------------
 *    Revisi�n   | Fecha      | Desarrollador                    | Resumen del cambio
 * ---------------------------------------------------------------------------------------
 *      1       | 17/10/2026 | Lic. Jos� Luis Arag�n Cervantes  | Creaci�n
 *      2       | 20/10/2026 | Lic. Jos� Luis Arag�n Cervantes  | Modelo de filas, UI en cm, alertas y �rea imprimible
 * ---------------------------------------------------------------------------------------*/

import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, FormControlLabel, MenuItem, Switch, Typography } from "@mui/material";
import TextField from "../ui/TextField";
import {
    CAMPOS_DETALLE,
    LABELS_DETALLE,
    SEPARADORES,
    dosDecimales,
    idDeObjeto as idDe,
    anchoObjetoMm,
    altoObjetoMm,
    anchoCatalogoMm,
    normalizarObjetoDetalle,
} from "../../settings/ticketLayoutCatalogs";
import { construirPlanSeccion } from "../../settings/ticketMotorLayout";
import {
    DOTS_POR_MM,
    CALIBRACION_POR_DEFECTO,
    dotsPorMmDe,
    MM_PX,
    altoLineaMm as altoLineaMmDe,
    multiplicadorFuenteTipo,
} from "../../settings/ticketPerfil";

const PX_POR_DOT = MM_PX / DOTS_POR_MM;   // 0.4725 px por dot (96 dpi en pantalla)
const ZOOM = 2;                          // Zoom de lectura del lienzo
const SNAP_DOTS = 4;                     // 0.5 mm
const SNAP_DOTS_FUERTE = 40;             // 5 mm
const ALTO_DISENO_MM = 500;              // lienzo util para disenar sin quedar atrapado
const TIPO_ARRASTRE = "application/x-sizesoftx-detalle";

const nuevoId = () => `det-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const mmToCm = (mm) => dosDecimales((Number(mm) || 0) / 10);
const cmToMm = (cm) => dosDecimales((Number(cm) || 0) * 10);

const detenerAtajos = (e) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
};

// Convierte el desplazamiento del puntero (px) a coordenadas del diseno (mm).
// pxPorDot es la escala horizontal real del lienzo (1 mm = DOTS_POR_MM dots);
// pxPorDotY permite compensar cuando el ancho se comprime por CSS pero el alto
// conserva su escala. El resultado se ancla a la cuadricula de 0.5 mm (4 dots).
export const convertirPunteroAMm = ({ offsetXPx, offsetYPx, pxPorDot, pxPorDotY, anchoPropioMm = 0, anchoMm, snapMm = 0.5, dotsPorMm = DOTS_POR_MM }) => {
    const escala = Number(pxPorDot) > 0 ? Number(pxPorDot) : (PX_POR_DOT * ZOOM);
    const escalaY = Number(pxPorDotY) > 0 ? Number(pxPorDotY) : escala;
    const xDots = (Number(offsetXPx) || 0) / escala;
    const yDots = (Number(offsetYPx) || 0) / escalaY;
    const xMax = Math.max(0, (Number(anchoMm) || 0) - (Number(anchoPropioMm) || 0));

    const anclar = (mm) => {
        const paso = Number(snapMm) > 0 ? Number(snapMm) : 0;
        const valor = paso > 0 ? Math.round(mm / paso) * paso : mm;
        return dosDecimales(valor);
    };

    return {
        xMm: anclar(Math.max(0, Math.min(xMax, xDots / dotsPorMm))),
        yMm: anclar(Math.max(0, Math.min(ALTO_DISENO_MM, yDots / dotsPorMm))),
    };
};

// Ancho util de un campo: desde su X hasta el borde del papel. Es lo que
// necesita el valor para no cortarse.
export const anchoUtilCampo = (objeto, anchoPapelMm) => dosDecimales(Math.max(5,
    (Number(anchoPapelMm) || 0) - (Number(objeto?.xMm) || 0)
));

export default function TicketDetalleDesigner({
    titulo = "Detalle por partida (objetos)",
    tituloLienzo = "Molde por partida (cent�metros)",
    ayudaCampo = "El valor de este campo lo asigna el sistema al imprimir el ticket.",
    campos = CAMPOS_DETALLE,
    labels = LABELS_DETALLE,
    separadores = SEPARADORES,
    columnas = 32,
    calibracion = CALIBRACION_POR_DEFECTO,
    objetos = [],
    onChange,
    seccion,
    seleccionExterna,
    onSeleccion,
}) {
    const [selectedIdLocal, setSelectedIdLocal] = useState(null);
    const controlado = Boolean(seccion && typeof onSeleccion === "function");
    const selectedId = controlado
        ? (seleccionExterna?.seccion === seccion ? (seleccionExterna?.id ?? null) : null)
        : selectedIdLocal;
    const seleccionar = (id) => {
        if (!controlado) setSelectedIdLocal(id);
        if (controlado) onSeleccion({ seccion, id: id ?? null });
    };
    const textoRef = useRef(null);
    const lienzoRef = useRef(null);
    const reglaRef = useRef(null);
    const scrollContainerRef = useRef(null);

    const infoDe = (campo) => campos.find((c) => c.campo === campo);
    const muestraDe = (campo) => infoDe(campo)?.muestra ?? campo;

    // Los objetos se normalizan igual que en la impresion (mismos defaults).
    const objetosNormalizados = useMemo(
        () => (objetos || []).map((o, i) => normalizarObjetoDetalle(o, i, campos)),
        [objetos, campos]
    );

    // PLAN del motor: es exactamente lo que se va a imprimir (con datos de muestra).
    // Se le pasa la MISMA calibracion que usa la impresion, para que el lienzo
    // y el papel coincidan milimetro a milimetro.
    const plan = useMemo(
        () => construirPlanSeccion({
            objetos: objetosNormalizados,
            textoDe: (campo) => muestraDe(campo),
            calibracion,
        }),
        [objetosNormalizados, campos, calibracion]
    );

    const indiceSeleccionado = objetosNormalizados.findIndex((o) => o.id === selectedId);
    const seleccionado = indiceSeleccionado >= 0 ? objetosNormalizados[indiceSeleccionado] : null;
    const indiceFilaSeleccionada = plan.filas.findIndex((f) => f.items.some((it) => it.objeto.id === selectedId));
    const filaSeleccionada = indiceFilaSeleccionada >= 0 ? plan.filas[indiceFilaSeleccionada] : null;
    const itemSeleccionado = filaSeleccionada
        ? filaSeleccionada.items.find((it) => it.objeto.id === selectedId) || null
        : null;

    const dotsPorMm = dotsPorMmDe(plan.calibracion || calibracion);
    const anchoCharDots = plan.calibracion?.anchoCaracterDots || 9;
    // El area imprimible REAL: columnas x ancho del caracter. Es el limite del papel
    // (con 9 dots por caracter, 32 columnas son 3.6 cm, no 4.8).
    const anchoUtilMmReal = (columnas * anchoCharDots) / dotsPorMm;   // en MILIMETROS
    // El lienzo se dibuja en MILIMETROS de papel (96 dpi de pantalla): el ancho
    // del lienzo es el ancho util en mm, NO los dots. Aqui estaba el error de escala.
    const anchoCanvas = anchoUtilMmReal * MM_PX * ZOOM;
    // Alto minimo de diseno con scroll (antes terminaba donde terminaba el
    // contenido y no habia espacio para arrastrar hacia abajo); si el contenido
    // crece mas alla, el lienzo se extiende para no ocultarlo.
    const altoCanvas = Math.max(ALTO_DISENO_MM, plan.altoTotalMm + 20) * MM_PX * ZOOM;
    // La vertical se mide en renglones (la unidad real de la impresora).
    const altoLineaMm = altoLineaMmDe(plan.calibracion || calibracion);
    const pxPorMm = MM_PX * ZOOM;
    const pxPorDotY = pxPorMm / dotsPorMm;
    // Escala horizontal real del papel: el ancho de un caracter es
    // anchoCaracterDots / dotsPorMm mm, no el de la fuente de diseno.
    const pxPorDotCalibrado = pxPorMm / dotsPorMm;
    const anchoUtilDotsReal = anchoUtilMmReal * dotsPorMm;
    // El texto se ve al doble y la caja de cada objeto queda a la cuarta parte del alto 1:1.
    const altoCajaPx = altoLineaMm * MM_PX * 0.25;

    const actualizarObjeto = (cambios) => {
        if (indiceSeleccionado < 0) return;
        onChange((objetos || []).map((o, i) => (i === indiceSeleccionado ? { ...o, ...cambios } : o)));
    };

    const eliminarSeleccionado = () => {
        if (indiceSeleccionado < 0) return;
        onChange((objetos || []).filter((o, i) => i !== indiceSeleccionado));
        seleccionar(null);
    };

    // Mover con el teclado: 0.5 mm (Shift = 5 mm) respetando la caja del papel.
    useEffect(() => {
        const onKeyDown = (event) => {
            if (!seleccionado) return;
            const tag = (event.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || tag === "select" || event.target?.isContentEditable) return;

            if (event.key === "Delete") {
                event.preventDefault();
                eliminarSeleccionado();
                return;
            }

            const paso = event.shiftKey ? SNAP_DOTS_FUERTE : SNAP_DOTS;
            const dx = event.key === "ArrowLeft" ? -paso : event.key === "ArrowRight" ? paso : 0;
            const dyLineas = event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0;
            if (!dx && !dyLineas) return;

            event.preventDefault();
            const anchoPropio = Number(seleccionado.anchoMm) || 0;
            const xMaxDots = Math.max(0, anchoUtilDotsReal - anchoPropio * dotsPorMm);
            const xDots = Math.max(0, Math.min(xMaxDots, (Number(seleccionado.xMm) || 0) * dotsPorMm + dx));
            // La vertical se mueve en RENGLONES (la unidad real): 1 renglon = 3.39 mm
            const yLineas = Math.max(0, (Number(seleccionado.yMm) || 0) / altoLineaMm + dyLineas);
            actualizarObjeto({
                xMm: dosDecimales(xDots / dotsPorMm),
                yMm: dosDecimales(yLineas * altoLineaMm),
            });
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [seleccionado, objetos, selectedId, onChange, anchoUtilDotsReal, altoLineaMm]);

    const crearObjeto = (payload, xMm, yMm) => {
        const info = payload.tipo === "campo" ? infoDe(payload.campo) : null;
        const anchoPropio = Number(info?.anchoMm) || (payload.tipo === "label" ? 40 : 30);
        const xClamp = Math.max(0, Math.min(anchoUtilMmReal - anchoPropio, Number(xMm) || 0));
        const yLineas = Math.max(0, Math.round((Number(yMm) || 0) / altoLineaMm));
        const nuevo = {
            id: nuevoId(),
            tipo: payload.tipo,
            ...(payload.tipo === "campo" ? { campo: payload.campo } : { texto: payload.texto ?? "" }),
            xMm: dosDecimales(xClamp),
            yMm: dosDecimales(Math.min(ALTO_DISENO_MM, yLineas * altoLineaMm)),
            anchoMm: anchoPropio,
            altoMm: Number(info?.altoMm) || 4,
            fuenteTipo: "A",
            negrita: false,
            alineacion: "left",
        };
        onChange([...(objetos || []), nuevo]);
        seleccionar(nuevo.id);
    };

    const insertarEnCola = (payload) => {
        const yMm = objetos?.length
            ? Math.min(500, Math.max(...objetos.map((o) => Number(o.yMm) || 0)) + 6)
            : 0;
        crearObjeto(payload, 0, yMm);
    };

    const iniciarArrastre = (e, payload, efecto) => {
        const data = JSON.stringify(payload);
        e.dataTransfer.effectAllowed = efecto;
        e.dataTransfer.setData(TIPO_ARRASTRE, data);
        e.dataTransfer.setData("text/plain", data);
    };

    // Traduce la posicion del puntero a coordenadas del diseno (mm).
    // La escala X sale del ancho REAL del contenido del lienzo (puede estar
    // comprimido por CSS); la Y conserva la escala fisica del papel.
    const posicionDesdeEvento = (e, anchoPropioMm = 0) => {
        const el = lienzoRef.current || e.currentTarget;
        const rect = el.getBoundingClientRect();
        const dotsAncho = Math.max(1, Math.round(anchoUtilMmReal * dotsPorMm));
        const contenidoAncho = Math.min(el.scrollWidth || anchoCanvas, anchoCanvas) || anchoCanvas;
        const pxPorDot = contenidoAncho > 0 ? contenidoAncho / dotsAncho : (PX_POR_DOT * ZOOM);

        const { xMm, yMm } = convertirPunteroAMm({
            offsetXPx: e.clientX - rect.left + (el.scrollLeft || 0),
            offsetYPx: e.clientY - rect.top + (el.scrollTop || 0),
            pxPorDot,
            pxPorDotY,
            anchoPropioMm,
            anchoMm: anchoUtilMmReal,
            dotsPorMm,
        });
        // La Y se ancla al renglon real de la impresora (3.39 mm aprox.).
        const yLineas = Math.max(0, Math.round(yMm / altoLineaMm));
        return { xMm, yMm: dosDecimales(Math.min(ALTO_DISENO_MM, yLineas * altoLineaMm)) };
    };

    const handleDropLienzo = (e) => {
        e.preventDefault();
        let payload = null;
        try {
            const data = e.dataTransfer.getData(TIPO_ARRASTRE) || e.dataTransfer.getData("text/plain");
            payload = JSON.parse(data || "{}");
        } catch {
            return;
        }

        if (payload.moverId) {
            const original = objetosNormalizados.find((o) => o.id === payload.moverId);
            const anchoPropio = Number(original?.anchoMm) || 0;
            const { xMm, yMm } = posicionDesdeEvento(e, anchoPropio);
            onChange((objetos || []).map((o, i) => (
                objetosNormalizados[i]?.id === payload.moverId
                    ? { ...o, xMm, yMm }
                    : o
            )));
            seleccionar(payload.moverId);
            return;
        }
        if (payload.tipo !== "campo" && payload.tipo !== "label") return;

        const info = payload.tipo === "campo" ? infoDe(payload.campo) : null;
        const anchoPropio = Number(info?.anchoMm) || (payload.tipo === "label" ? 40 : 30);
        const { xMm, yMm } = posicionDesdeEvento(e, anchoPropio);
        crearObjeto(payload, xMm, yMm);
    };

    const nombreObjeto = (o) => {
        if (o.tipo === "campo") return `Campo: ${infoDe(o.campo)?.etiqueta ?? o.campo}`;
        const t = (o.texto ?? "").trim();
        return `Label: ${t ? (t.length > 14 ? `${t.slice(0, 14)}...` : t) : "(vac�o)"}`;
    };

    const finFilaMm = (fila) => (
        fila.yLineas + Math.max(1, ...fila.items.map((item) => item.lineas.length))
    ) * altoLineaMm;

    // Espacio antes de una fila, en milimetros (derivado del flujo real).
    const espacioAntesFila = (indice) => {
        if (indice <= 0) return 0;
        const previa = plan.filas[indice - 1];
        const actual = plan.filas[indice];
        return actual.yMm - finFilaMm(previa);
    };

    // Ajustar el "espacio antes" mueve el ancla de la fila completa.
    const moverEspacioAntes = (indice, mm) => {
        if (indice <= 0) return;
        const previa = plan.filas[indice - 1];
        const baseMm = finFilaMm(previa);
        const fila = plan.filas[indice];
        const idsFila = fila.items.map((it) => it.objeto.id);
        const desplazamientoMm = (baseMm + Math.max(0, Number(mm) || 0)) - fila.yMm;
        onChange((objetos || []).map((o, i) => {
            const normalizado = objetosNormalizados[i];
            if (!normalizado || !idsFila.includes(normalizado.id)) return o;
            return { ...o, yMm: dosDecimales((Number(normalizado.yMm) || 0) + desplazamientoMm) };
        }));
    };

    return (
        <Box sx={{ p: 1, border: "1px solid #ddd", borderRadius: 1 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography variant="caption" sx={{ fontWeight: "bold" }}>{titulo}</Typography>
            </Box>


            <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={1} sx={{ mt: 1 }}>
                <Box gridColumn="span 6">
                    <Box sx={{ p: 1, border: "1px solid #ddd", borderRadius: 1, height: "100%" }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>{tituloLienzo}</Typography>
                        {/* Regla fuera del contenedor de scroll (pegada arriba, no cubre el texto) */}
                        <Box
                            ref={reglaRef}
                            aria-hidden
                            sx={{
                                width: `${anchoCanvas}px`,
                                overflowX: "hidden",
                                maxWidth: "100%",
                                mx: "auto",
                                height: 18,
                                borderBottom: "1px solid #90a4ae",
                                backgroundColor: "#fafafa",
                                pointerEvents: "none",
                            }}
                        >
                            {Array.from({ length: Math.floor(anchoUtilMmReal / 10 / 0.5) + 1 }).map((_, i) => {
                                const cm = i * 0.5;
                                return (
                                    <Box
                                        key={i}
                                        sx={{
                                            position: "absolute",
                                            left: `${cm * 10 * pxPorMm}px`,
                                            top: 0,
                                            height: i % 2 === 0 ? 11 : 6,
                                            borderLeft: "1px solid #90a4ae",
                                        }}
                                    >
                                        {i % 2 === 0 && (
                                            <span style={{ fontSize: 8, color: "#607d8b", paddingLeft: 2 }}>{cm.toFixed(0)}</span>
                                        )}
                                    </Box>
                                );
                            })}
                            <span style={{ position: "absolute", right: 2, top: 2, fontSize: 8, color: "#607d8b" }}>cm</span>
                        </Box>
                        <Box
                            ref={scrollContainerRef}
                            onScroll={(e) => {
                                if (reglaRef.current) reglaRef.current.scrollLeft = e.target.scrollLeft;
                            }}
                            sx={{
                                mt: 1,
                                width: `${anchoCanvas}px`,
                                maxWidth: "100%",
                                mx: "auto",
                                overflowX: "auto",
                            }}
                        >
                            <Box
                                ref={lienzoRef}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = e.dataTransfer.effectAllowed === "move" ? "move" : "copy";
                                    // Auto-scroll al acercarse a los bordes del lienzo.
                                    const el = lienzoRef.current;
                                    if (!el) return;
                                    const rect = el.getBoundingClientRect();
                                    if (e.clientY - rect.top < 36) el.scrollTop -= 16;
                                    else if (rect.bottom - e.clientY < 36) el.scrollTop += 16;
                                }}
                                onDrop={handleDropLienzo}
                                onClick={(e) => {
                                    if (e.target === e.currentTarget) seleccionar(null);
                                }}
                                    sx={{
                                    position: "relative",
                                    width: `${anchoCanvas}px`,
                                    height: `${altoCanvas}px`,
                                    maxHeight: "460px",
                                    backgroundColor: "#fff",
                                    backgroundImage: "linear-gradient(to right, #eee 1px, transparent 1px), linear-gradient(to bottom, #eee 1px, transparent 1px)",
                                    backgroundSize: `${5 * pxPorMm}px ${5 * pxPorMm}px`,
                                    border: "1px solid #ccc",
                                    borderTop: 0,
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                }}
                            >
                            {plan.filas.length === 0 && (
                                <Typography variant="caption" sx={{ position: "absolute", top: 24, left: "50%", transform: "translateX(-50%)", color: "#999", pointerEvents: "none" }}>
                                    Arrastra objetos aqu�
                                </Typography>
                            )}

                            {/* Banda de cada fila: alto reservado y posicion real de impresion */}
                            {plan.filas.map((fila, indiceFila) => (
                                <Box
                                    key={`fila-${indiceFila}`}
                                    sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: `${fila.yMm * pxPorMm}px`,
                                        width: `${anchoCanvas}px`,
                                        height: `${Math.max(2, fila.altoReservadoLineas * altoLineaMm * pxPorMm)}px`,
                                        borderTop: "1px dashed #bdbdbd",
                                        backgroundColor: indiceFila % 2 === 0 ? "rgba(25,118,210,0.04)" : "transparent",
                                        pointerEvents: "none",
                                    }}
                                />
                            ))}

                            {/* Objetos: se dibujan con el texto REAL ya partido */}
                            {plan.filas.map((fila) => fila.items.map((item) => {
                                const o = item.objeto;
                                const fueraDeArea = (Number(o.xMm) || 0) * dotsPorMm + (Number(o.anchoMm) || 0) * dotsPorMm > anchoUtilDotsReal;
                                const estaSeleccionado = o.id === selectedId;
                                return (
                                    <div
                                        key={o.id}
                                        draggable
                                        title={o.tipo === "label" ? "Doble clic para editar el texto" : undefined}
                                        onDragStart={(e) => iniciarArrastre(e, { moverId: o.id }, "move")}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            seleccionar(o.id);
                                        }}
                                        onDoubleClick={(e) => {
                                            e.stopPropagation();
                                            seleccionar(o.id);
                                            if (o.tipo === "label") {
                                                setTimeout(() => {
                                                    const el = textoRef.current;
                                                    if (el) {
                                                        el.focus();
                                                        el.select?.();
                                                    }
                                                }, 80);
                                            }
                                        }}
                                        style={{
                                            position: "absolute",
                                            left: `${(Number(o.xMm) || 0) * pxPorMm}px`,
                                            top: `${fila.yMm * pxPorMm}px`,
                                            width: `${(Number(o.anchoMm) || 0) * pxPorMm}px`,
                                            minHeight: `${Math.max(2, Math.max(item.lineas.length, item.lineasReservadas) * altoCajaPx)}px`,
                                            fontFamily: '"Courier New", Courier, monospace',
                                            // El ancho del caracter en pantalla debe ser el real de
                                            // la impresora (dots), no la altura del glifo.
                                            fontSize: `${dosDecimales((anchoCharDots * multiplicadorFuenteTipo(o.fuenteTipo) * pxPorDotCalibrado) / 0.6)}px`,
                                            lineHeight: `${altoCajaPx}px`,
                                            fontWeight: o.negrita ? "bold" : "normal",
                                            // Labels y campos respetan la alineacion dentro de su caja.
                                            textAlign: o.alineacion ?? "left",
                                            whiteSpace: "pre",
                                            // El texto siempre se muestra completo, aunque
                                            // el ancho visual de la caja sea menor.
                                            overflow: "visible",
                                            cursor: "move",
                                            userSelect: "none",
                                            border: fueraDeArea ? "1px solid #d32f2f" : (estaSeleccionado ? "1px solid #1976d2" : "1px dashed #d5d5d5"),
                                            outline: estaSeleccionado ? "2px solid #1976d2" : "none",
                                        }}
                                    >
                                        {item.lineas.map((linea, k) => (
                                            <div key={k}>{linea}</div>
                                        ))}
                                    </div>
                                );
                            }))}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box gridColumn="span 3">
                    <Box sx={{ p: 1, border: "1px solid #ddd", borderRadius: 1, height: "100%" }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>Objetos disponibles</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">Arrastra al molde de la izquierda.</Typography>

                        <Typography variant="caption" display="block" sx={{ mt: 1, color: "#666" }}>Campos</Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {campos.map((c) => (
                                <Chip
                                    key={c.campo}
                                    size="small"
                                    label={c.etiqueta}
                                    draggable
                                    onDragStart={(e) => iniciarArrastre(e, { tipo: "campo", campo: c.campo }, "copy")}
                                    onClick={() => insertarEnCola({ tipo: "campo", campo: c.campo })}
                                    title="Arrastra o haz clic para insertar"
                                    sx={{ cursor: "grab" }}
                                />
                            ))}
                        </Box>

                        <Typography variant="caption" display="block" sx={{ mt: 1, color: "#666" }}>Labels para valores (editables)</Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {labels.map((texto, i) => (
                                <Chip
                                    key={`label-${i}`}
                                    size="small"
                                    variant="outlined"
                                    label={texto || "(libre)"}
                                    draggable
                                    onDragStart={(e) => iniciarArrastre(e, { tipo: "label", texto }, "copy")}
                                    onClick={() => insertarEnCola({ tipo: "label", texto })}
                                    title="Arrastra o haz clic para insertar"
                                    sx={{ cursor: "grab" }}
                                />
                            ))}
                        </Box>

                        <Typography variant="caption" display="block" sx={{ mt: 1, color: "#666" }}>Separadores</Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {separadores.map((s, i) => (
                                <Chip
                                    key={`sep-${i}`}
                                    size="small"
                                    variant="outlined"
                                    label={s.vista}
                                    draggable
                                    onDragStart={(e) => iniciarArrastre(e, { tipo: "label", texto: s.texto }, "copy")}
                                    onClick={() => insertarEnCola({ tipo: "label", texto: s.texto })}
                                    title="Arrastra o haz clic para insertar"
                                    sx={{ cursor: "grab" }}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>

                <Box gridColumn="span 3">
                    <Box sx={{ p: 1, border: "1px solid #ddd", borderRadius: 1, height: "100%" }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>Propiedades del objeto</Typography>
                        <TextField
                            autoComplete="off" onKeyDown={detenerAtajos}
                            select
                            label="Objeto"
                            size="small"
                            fullWidth
                            value={seleccionado ? selectedId : ""}
                            onChange={(e) => seleccionar(e.target.value)}
                            sx={{ mt: 1 }}
                        >
                            {objetosNormalizados.length === 0 && <MenuItem value="" disabled>Sin objetos</MenuItem>}
                            {objetosNormalizados.map((o, i) => (
                                <MenuItem key={idDe(o, i)} value={o.id}>{`${i + 1}. ${nombreObjeto(o)}`}</MenuItem>
                            ))}
                        </TextField>

                        {!seleccionado && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                Selecciona un objeto del molde o del combo.
                            </Typography>
                        )}

                        {seleccionado && (
                            <>
                                <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1} sx={{ mt: 1 }}>
                                    <TextField
                                        autoComplete="off" onKeyDown={detenerAtajos}
                                        label="X (cm)"
                                        size="small"
                                        type="number"
                                        step="0.1"
                                        onFocus={(e) => e.target.select()}
                                        value={mmToCm(seleccionado.xMm)}
                                        onChange={(e) => {
                                            const n = Number(e.target.value);
                                            if (Number.isNaN(n)) return;
                                            actualizarObjeto({ xMm: cmToMm(Math.max(0, Math.min(anchoUtilMmReal, n))) });
                                        }}
                                    />
                                    <TextField
                                        autoComplete="off" onKeyDown={detenerAtajos}
                                        label="Y ancla (cm)"
                                        size="small"
                                        type="number"
                                        step="0.1"
                                        onFocus={(e) => e.target.select()}
                                        value={mmToCm(seleccionado.yMm)}
                                        onChange={(e) => {
                                            const n = Number(e.target.value);
                                            if (Number.isNaN(n)) return;
                                            const yMm = cmToMm(Math.max(0, Math.min(ALTO_DISENO_MM, n)));
                                            const yLineas = Math.round(yMm / altoLineaMm);
                                            actualizarObjeto({ yMm: dosDecimales(Math.min(ALTO_DISENO_MM, yLineas * altoLineaMm)) });
                                        }}
                                        helperText="Se ajusta al rengl�n real de la impresora"
                                    />
                                    <TextField
                                        autoComplete="off" onKeyDown={detenerAtajos}
                                        label="Ancho (cm)"
                                        size="small"
                                        type="number"
                                        step="0.1"
                                        onFocus={(e) => e.target.select()}
                                        value={mmToCm(anchoObjetoMm(seleccionado, campos))}
                                        onChange={(e) => {
                                            const n = Number(e.target.value);
                                            if (Number.isNaN(n)) return;
                                            actualizarObjeto({ anchoMm: cmToMm(Math.max(0.5, Math.min(anchoUtilMmReal, n))) });
                                        }}
                                        helperText="El ancho solo ajusta la caja; el texto se imprime completo"
                                    />
                                    <TextField
                                        autoComplete="off" onKeyDown={detenerAtajos}
                                        label="Alto (cm)"
                                        size="small"
                                        type="number"
                                        step="0.1"
                                        onFocus={(e) => e.target.select()}
                                        value={mmToCm(altoObjetoMm(seleccionado, campos))}
                                        onChange={(e) => {
                                            const n = Number(e.target.value);
                                            if (Number.isNaN(n)) return;
                                            actualizarObjeto({ altoMm: cmToMm(Math.max(0.2, Math.min(10, n))) });
                                        }}
                                        helperText="La caja crece sola para mostrar todo el texto"
                                    />
                                    <TextField
                                        autoComplete="off" onKeyDown={detenerAtajos}
                                        select
                                        label="Tipo fuente"
                                        size="small"
                                        value={seleccionado.fuenteTipo ?? "A"}
                                        onChange={(e) => actualizarObjeto({ fuenteTipo: e.target.value })}
                                    >
                                        <MenuItem value="A">Font A (12x24)</MenuItem>
                                        <MenuItem value="B">Font B (9x17)</MenuItem>
                                        <MenuItem value="D">Font D (24x48)</MenuItem>
                                    </TextField>
                                    <TextField
                                        autoComplete="off" onKeyDown={detenerAtajos}
                                        select
                                        label="Alineaci�n"
                                        size="small"
                                        value={seleccionado.alineacion ?? "left"}
                                        onChange={(e) => actualizarObjeto({ alineacion: e.target.value })}
                                    >
                                        <MenuItem value="left">Izquierda</MenuItem>
                                        <MenuItem value="center">Centro</MenuItem>
                                        <MenuItem value="right">Derecha</MenuItem>
                                    </TextField>
                                </Box>
                                <FormControlLabel
                                    control={<Switch size="small" checked={Boolean(seleccionado.negrita)} onChange={(e) => actualizarObjeto({ negrita: e.target.checked })} />}
                                    label="Negrita"
                                />
                                {itemSeleccionado && (
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {`${itemSeleccionado.lineas.length} rengl��n(es) completos; la caja crece con el texto.`}
                                    </Typography>
                                )}
                                <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => actualizarObjeto({ anchoMm: anchoCatalogoMm(seleccionado, campos) })}
                                    >
                                        Ancho del cat�logo ({mmToCm(anchoCatalogoMm(seleccionado, campos)).toFixed(2)} cm)
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => actualizarObjeto({ altoMm: 4 })}
                                    >
                                        Alto de 1 rengl�n
                                    </Button>
                                </Box>
                                {seleccionado.tipo === "campo" ? (
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                        {ayudaCampo}
                                    </Typography>
                                ) : (
                                    <TextField
                                        autoComplete="off" onKeyDown={detenerAtajos}
                                        inputRef={textoRef}
                                        label="Texto"
                                        size="small"
                                        fullWidth
                                        value={seleccionado.texto ?? ""}
                                        onChange={(e) => actualizarObjeto({ texto: e.target.value })}
                                        helperText="Editable: complementa el campo de valor"
                                    />
                                )}

                                {filaSeleccionada && (
                                    <Box sx={{ mt: 1, p: 1, border: "1px solid #e0e0e0", borderRadius: 1, backgroundColor: "#fafafa" }}>
                                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>Fila</Typography>
                                        <TextField
                                            autoComplete="off" onKeyDown={detenerAtajos}
                                            label="Espacio antes (cm)"
                                            size="small"
                                            type="number" onFocus={(e) => e.target.select()}
                                            fullWidth
                                            sx={{ mt: 0.5 }}
                                            value={espacioAntesFila(indiceFilaSeleccionada).toFixed(2)}
                                            onChange={(e) => {
                                                const n = Number(e.target.value);
                                                if (Number.isNaN(n)) return;
                                                moverEspacioAntes(indiceFilaSeleccionada, Math.round(n * 80));
                                            }}
                                            helperText={`Impresi�n en ${filaSeleccionada.yMm.toFixed(2)} cm � el renglon se imprime completo`}
                                        />
                                    </Box>
                                )}

                                <Button color="error" variant="outlined" size="small" sx={{ mt: 1 }} onClick={eliminarSeleccionado}>
                                    Eliminar objeto
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
